"use client"

/**
 * Shelf UI illustrations — miniature app surfaces composed from tokens, not
 * marketing screenshots and not wireframe SVGs.
 *
 * Reads like the template-gallery mockups in Asana's workflow cards: filled
 * chrome, sidebar strip, and a product-specific focal in the brand hue.
 */

import type { Product } from "@/contexts/product-context"
import { cn } from "@/lib/utils"

function BrandBlock({
  brandColor,
  className,
  style,
}: {
  brandColor: string
  className?: string
  style?: React.CSSProperties
}) {
  return (
    <span
      aria-hidden
      className={className}
      style={{
        backgroundColor: `color-mix(in oklch, ${brandColor} 55%, var(--card))`,
        ...style,
      }}
    />
  )
}

function WindowDots() {
  return (
    <div aria-hidden className="flex items-center gap-1 px-2.5 py-2">
      <span className="size-1.5 rounded-full bg-foreground/15" />
      <span className="size-1.5 rounded-full bg-foreground/12" />
      <span className="size-1.5 rounded-full bg-foreground/10" />
    </div>
  )
}

function MiniShell({
  brandColor,
  children,
  className,
  embedded,
}: {
  brandColor: string
  children: React.ReactNode
  className?: string
  /** Sits on a brand-tinted parent — no second card-colored window. */
  embedded?: boolean
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border",
        embedded
          ? "border-foreground/10 shadow-none"
          : "border-border/60 bg-card shadow-sm",
        className,
      )}
    >
      <div
        className={cn(
          "border-b border-border/50",
          embedded ? "bg-foreground/[0.06]" : "bg-muted/25",
        )}
      >
        <WindowDots />
      </div>
      <div className="flex min-h-32">
        <div
          aria-hidden
          className={cn(
            "flex w-[22%] flex-col gap-1.5 border-r border-border/50 p-2",
            embedded ? "bg-foreground/[0.04]" : "bg-muted/15",
          )}
        >
          <BrandBlock brandColor={brandColor} className="h-1.5 w-full rounded-sm opacity-80" />
          <span className="h-1 w-[85%] rounded-sm bg-foreground/8" />
          <span className="h-1 w-[70%] rounded-sm bg-foreground/6" />
          <span className="mt-1 h-1 w-[90%] rounded-sm bg-foreground/6" />
          <span className="h-1 w-[60%] rounded-sm bg-foreground/6" />
        </div>
        <div className="min-w-0 flex-1 p-2.5">{children}</div>
      </div>
    </div>
  )
}

function HubTableScene({ brandColor }: { brandColor: string }) {
  return (
    <>
      <div className="mb-2 flex items-center gap-1.5">
        <span className="h-2 w-14 rounded-sm bg-foreground/12" />
        <BrandBlock brandColor={brandColor} className="ml-auto h-4 w-10 rounded-sm" />
      </div>
      <div className="space-y-1.5">
        {[0.95, 0.82, 0.88, 0.76].map((width, index) => (
          <div
            key={width}
            className="flex items-center gap-1.5 rounded-md border border-border/40 bg-background px-1.5 py-1"
          >
            <span className="size-2 shrink-0 rounded-full bg-foreground/10" />
            <span
              className="h-1.5 rounded-sm bg-foreground/10"
              style={{ width: `${width * 100}%` }}
            />
            {index === 1 ? (
              <BrandBlock brandColor={brandColor} className="ml-auto h-2 w-6 rounded-full" />
            ) : (
              <span className="ml-auto h-1.5 w-5 rounded-full bg-foreground/8" />
            )}
          </div>
        ))}
      </div>
    </>
  )
}

function ExamScene({ brandColor }: { brandColor: string }) {
  return (
    <>
      <div className="mb-2 flex items-center gap-1.5">
        <span className="h-2 w-16 rounded-sm bg-foreground/12" />
        <BrandBlock brandColor={brandColor} className="ml-auto size-4 rounded-full" />
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <div className="space-y-1.5 rounded-md border border-border/40 bg-background p-1.5">
          {[1, 2, 3].map(row => (
            <div key={row} className="flex items-center gap-1.5">
              <BrandBlock brandColor={brandColor} className="size-2.5 rounded-sm" />
              <span className="h-1.5 flex-1 rounded-sm bg-foreground/10" />
            </div>
          ))}
        </div>
        <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-border/40 bg-muted/20 px-2 py-1">
          <BrandBlock brandColor={brandColor} className="size-5 rounded-full" />
          <span className="h-1 w-5 rounded-sm bg-foreground/10" />
        </div>
      </div>
    </>
  )
}

function SurveysScene({ brandColor }: { brandColor: string }) {
  return (
    <>
      <div className="mb-2 flex items-center gap-1">
        {[1, 2, 3, 4].map(dot => (
          <span
            key={dot}
            className={cn(
              "size-2.5 rounded-full border border-border/50",
              dot === 3 ? "" : "bg-foreground/8",
            )}
            style={
              dot === 3
                ? { backgroundColor: `color-mix(in oklch, ${brandColor} 65%, var(--card))` }
                : undefined
            }
          />
        ))}
      </div>
      <div className="flex items-end gap-1.5 rounded-md border border-border/40 bg-background px-2 pb-2 pt-3">
        {[0.45, 0.72, 1, 0.58].map((height, index) => (
          <BrandBlock
            key={height}
            brandColor={brandColor}
            className="w-3 rounded-sm"
            style={{
              height: `${height * 2.75}rem`,
              opacity: index === 2 ? 1 : 0.55 + index * 0.1,
            }}
          />
        ))}
      </div>
    </>
  )
}

function DashboardScene({ brandColor }: { brandColor: string }) {
  return (
    <>
      <div className="mb-2 grid grid-cols-3 gap-1.5">
        {[1, 2, 3].map(tile => (
          <div
            key={tile}
            className="rounded-md border border-border/40 bg-background px-1 py-1.5"
          >
            <span className="mb-1 block h-1 w-6 rounded-sm bg-foreground/10" />
            <BrandBlock
              brandColor={brandColor}
              className="block h-2 w-8 rounded-sm"
              style={{ opacity: tile === 2 ? 1 : 0.65 }}
            />
          </div>
        ))}
      </div>
      <div className="rounded-md border border-border/40 bg-background p-1.5">
        <div className="flex items-end gap-1">
          {[0.35, 0.55, 0.8, 0.5, 0.65].map(h => (
            <BrandBlock
              key={h}
              brandColor={brandColor}
              className="w-2.5 rounded-sm"
              style={{ height: `${h * 2.25}rem`, opacity: 0.45 + h * 0.5 }}
            />
          ))}
        </div>
      </div>
    </>
  )
}

function MappingScene({ brandColor }: { brandColor: string }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1 rounded-md border border-border/40 bg-background p-1.5">
        {[1, 2, 3].map(row => (
          <span key={row} className="block h-1.5 rounded-sm bg-foreground/10" style={{ width: `${100 - row * 12}%` }} />
        ))}
      </div>
      <div className="flex flex-col justify-center gap-1.5">
        <BrandBlock brandColor={brandColor} className="h-2 w-full rounded-sm" />
        <span className="block h-1.5 w-[80%] rounded-sm bg-foreground/10" />
        <BrandBlock brandColor={brandColor} className="h-2 w-[65%] rounded-sm opacity-70" />
      </div>
    </div>
  )
}

const SCENE_BY_PRODUCT: Partial<
  Record<Product, (props: { brandColor: string }) => React.JSX.Element>
> = {
  "exxat-exam-management": ExamScene,
  "exxat-surveys": SurveysScene,
  "exxat-curriculum-mapping": MappingScene,
  "exxat-accreditation": MappingScene,
  "exxat-student-success": DashboardScene,
  "exxat-compliance": HubTableScene,
  "exxat-prism": DashboardScene,
  "exxat-one-schools": HubTableScene,
  "exxat-one-sites": HubTableScene,
}

export function ProductShelfUiIllustration({
  product,
  brandColor,
  className,
  embedded,
}: {
  product: Product
  brandColor: string
  className?: string
  embedded?: boolean
}) {
  const Scene = SCENE_BY_PRODUCT[product] ?? HubTableScene

  return (
    <div aria-hidden className={cn("w-full", className)}>
      <MiniShell brandColor={brandColor} embedded={embedded}>
        <Scene brandColor={brandColor} />
      </MiniShell>
    </div>
  )
}
